/* eslint-disable react/jsx-one-expression-per-line */

import { useRef, useState, useEffect, FC, memo, useCallback } from 'react';
import {
  Button,
  Box,
  Heading,
  IconButton,
  Text,
  Spinner,
  Flex,
  Separator,
  Drawer,
  Dialog,
} from '@chakra-ui/react';
import { FiInfo } from 'react-icons/fi';
import { OmniAppHeaderControlButton } from './ui/appheader';
// AWS Amplify v6 imports
import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import { IUpdatedQuery } from '../context/queryBuilderProvider';
import { getHistoryList } from '../graphql/queries';
import { subscribeToAddHistory } from '../graphql/subscriptions';

// AWS Amplify v6 client
const client = generateClient();

// AWS Amplify v6 types
interface GraphQLResult<T = unknown> {
  data: T;
  errors?: Array<{ message: string }>;
}

interface SubscriptionData {
  data?: {
    subscribeToAddHistory?: IHistoryItem;
  };
}

interface Observable<T> {
  subscribe: (observer: {
    next?: (data: T) => void;
    error?: (error: unknown) => void;
    complete?: () => void;
  }) => {
    unsubscribe: () => void;
  };
}

interface Client {
  id: string;
  name: string;
  client?: string; // Alternative property name used in some data
  [key: string]: unknown;
}

interface MetricItem {
  id: string;
  name: string;
  type?: string;
  [key: string]: unknown;
}

interface DimensionItem {
  id: string;
  name: string;
  type?: string;
  [key: string]: unknown;
}

interface FilterItem {
  id: string;
  name: string;
  column?: string; // Alternative property name used in some data
  type?: string;
  values?: unknown[];
  [key: string]: unknown;
}

interface IHistoryItem {
  source: string;
  submitted: boolean;
  query: string;
  id: string;
}

export interface IJsonQuery {
  viewName: string;
  id: string;
  clients: Client[];
  query: {
    metrics: MetricItem[];
    dimensions: DimensionItem[];
    filters: FilterItem[];
  };
}

const HistoryDrawer: FC<{
  updateFunction: (query: IJsonQuery | IUpdatedQuery) => string;
  userName: string;
}> = ({ updateFunction, userName, ...props }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const btnRef = useRef<HTMLButtonElement>(null);

  const [historyItems, setHistoryItems] = useState<IHistoryItem[]>([]);
  const [jsonQueries, setJsonQueries] = useState<IJsonQuery[]>([]);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [selectedQuery, setSelectedQuery] = useState<IJsonQuery | null>(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState('');

  // const handleChange = (event) => {
  //   console.log('UserName changed', event.target.value);
  //   setNextToken(null);
  //   setHistoryItems([]);
  //   // setUserName(userList.find((u) => u.name === event.target.value));
  // };

  interface IHistoryList {
    getHistoryList: {
      items: IHistoryItem[];
      nextToken?: string;
    };
  }

  const getHistory = useCallback(async () => {
    setIsLoadingHistory(true);
    try {
      // AWS Amplify v6 GraphQL API call
      const getData = (await client.graphql({
        query: getHistoryList,
        variables: {
          nextToken,
        },
      })) as GraphQLResult<IHistoryList>;

      const ReceivedData = getData.data;
      if (!ReceivedData) {
        throw new Error('No History data returned');
      }

      setHistoryItems((p) => [...p, ...ReceivedData.getHistoryList.items]);
      setNextToken(
        ReceivedData.getHistoryList.nextToken
          ? ReceivedData.getHistoryList.nextToken
          : null,
      );
    } catch (error) {
      console.error('Error fetching history:', error);
      // Fallback to empty state if API fails
      setHistoryItems([]);
      setNextToken(null);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [nextToken]);

  const subScribeToQueries = useCallback(() => {
    try {
      // AWS Amplify v6 GraphQL subscription
      const subscription = (
        client.graphql({
          query: subscribeToAddHistory,
          variables: {
            owner: userName,
          },
        }) as Observable<SubscriptionData>
      ).subscribe({
        next: (data: SubscriptionData) => {
          // Add new item to history if it's for the current user
          if (data?.data?.subscribeToAddHistory) {
            const newItem = data.data.subscribeToAddHistory;
            setHistoryItems((prev) => [newItem, ...prev]);
          }
        },
        error: (error: unknown) => {
          console.error('Subscription error:', error);
        },
      });

      return () => {
        subscription.unsubscribe();
      };
    } catch (error) {
      console.error('Error setting up subscription:', error);
      // Return empty cleanup function if subscription fails
      return () => {
        // Mock subscription cleanup
      };
    }
  }, [userName]);

  useEffect(() => {
    setNextToken(null);
    setHistoryItems([]);
    getHistory();
    subScribeToQueries();
  }, [userName, getHistory, subScribeToQueries]);

  const getSelectedQuery = useCallback(
    async (selectedId: string) => {
      const item = historyItems.find((e) => e.id === selectedHistoryId);
      if (!item) {
        throw new Error('No item selected');
      }

      const [, , source] = item.query.split('/');

      // Get the query from S3

      const linkResult = await getUrl({
        path: source,
      });
      const link = linkResult.url;

      fetch(link)
        .then((res) => res.json())
        .then((out) => {
          const newQuery = { id: selectedId, ...out };
          setJsonQueries((p) => [...p, newQuery]);
          setSelectedQuery(newQuery);
        })
        .catch((err) => console.error(err));
    },
    [historyItems, selectedHistoryId],
  );

  useEffect(() => {
    if (selectedHistoryId) {
      const xx = jsonQueries.some((e) => e.id === selectedHistoryId);
      if (!xx) {
        setSelectedQuery(null);
        getSelectedQuery(selectedHistoryId);
      } else {
        const foundJsonQuery = jsonQueries.find(
          (e) => e.id === selectedHistoryId,
        );
        if (!foundJsonQuery) {
          console.warn(
            `No JsonQuery found with id ${selectedHistoryId}`,
            jsonQueries,
          );
          return;
        }
        setSelectedQuery(foundJsonQuery);
      }
    }
  }, [selectedHistoryId, getSelectedQuery, jsonQueries]);

  const Clients: FC<{ itemType: string }> = memo(({ itemType, ...rest }) => {
    Clients.displayName = 'Clients';

    const mapLabels = (caption: string, index: number) => (
      <Box
        m={1}
        key={index}
        bg="transparent"
        borderWidth="1px"
        borderColor="#edf0f5"
        rounded="sm"
        shadow="sm"
      >
        <Text px={1} color="#2d3748">
          {caption}
        </Text>
      </Box>
    );

    function getSelectedQueryItems() {
      if (!selectedQuery) {
        throw new Error('No selected query');
      }
      switch (itemType) {
        case 'Clients':
          return selectedQuery.clients.map((client, index) =>
            mapLabels(
              String(client.client || client.name || 'Unknown Client'),
              index,
            ),
          );
        case 'Metrics':
          return selectedQuery.query.metrics.map((metric, index) =>
            mapLabels(String(metric.name || 'Unknown Metric'), index),
          );
        case 'Dimensions':
          return selectedQuery.query.dimensions.map((dimension, index) =>
            mapLabels(String(dimension.name || 'Unknown Dimension'), index),
          );
        case 'Filters':
          return selectedQuery.query.filters.map((filter, index) =>
            mapLabels(
              String(filter.column || filter.name || 'Unknown Filter'),
              index,
            ),
          );

        default:
          console.error(`Cannot find the itemType ${itemType}`);
          return null;
      }
    }

    return (
      <Flex
        flexWrap="wrap"
        justify="flex-end"
        // spacing={1}
        // isInline
        overflowX="auto"
        {...rest}
      >
        {getSelectedQueryItems()}
      </Flex>
    );
  });

  const callUpdateFunction = () => {
    if (!selectedQuery) {
      return;
    }
    updateFunction(selectedQuery);
    setIsDrawerOpen(false);
  };

  const ModalItem: FC<{ header: string }> = ({ header, ...prop }) => (
    <Flex gap={3} justify="space-between" align="center" {...prop}>
      <Heading size="sm">{header}</Heading>
      <Clients itemType={header} />
    </Flex>
  );

  return (
    <Box {...props}>
      <Flex gap={1} align="center">
        <OmniAppHeaderControlButton
          ref={btnRef}
          variant="contrast"
          size="sm"
          onClick={() => setIsDrawerOpen(true)}
        >
          History
        </OmniAppHeaderControlButton>

        <Text p={3} fontSize="xl" fontStyle="italic" color="#a2a9ad">
          {userName}
        </Text>
      </Flex>

      <Drawer.Root
        open={isDrawerOpen}
        placement="end"
        onOpenChange={(details: { open: boolean }) =>
          setIsDrawerOpen(details.open)
        }
      >
        <Drawer.Backdrop />
        <Drawer.Content>
          <Drawer.CloseTrigger />
          <Drawer.Header color="#00a1d2">
            History for {userName}
          </Drawer.Header>

          <Drawer.Body overflowY="auto">
            <Dialog.Root
              open={isModalOpen}
              onOpenChange={(details: { open: boolean }) =>
                setIsModalOpen(details.open)
              }
              size="xl"
            >
              <Dialog.Content>
                <Dialog.Header bg="bg.brand" color="fg.inverse">
                  {selectedQuery ? selectedQuery.viewName : 'Not found'}
                </Dialog.Header>
                <Dialog.CloseTrigger color="fg.inverse" />
                <Dialog.Body
                  m={4}
                  bg="bg.surface"
                  rounded="md"
                  border="1px"
                  borderColor="#edf0f5"
                  shadow="lg"
                >
                  <Box>
                    {selectedQuery ? (
                      <Flex direction="column" gap={4}>
                        <ModalItem header="Clients" />
                        <Separator />
                        <ModalItem header="Metrics" />
                        <Separator />
                        <ModalItem header="Dimensions" />
                        <Separator />
                        <ModalItem header="Filters" />
                      </Flex>
                    ) : (
                      <Flex gap={2} align="center">
                        <Spinner
                          borderWidth="4px"
                          animationDuration="0.65s"
                          color="#00a1d2"
                          size="xl"
                        />
                        <Text>...cannot find query</Text>
                      </Flex>
                    )}
                  </Box>
                </Dialog.Body>
                <Dialog.Footer>
                  <Dialog.ActionTrigger asChild>
                    <Button variant="ghost" size="md" mr={3}>
                      Close
                    </Button>
                  </Dialog.ActionTrigger>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={callUpdateFunction}
                  >
                    Load query
                  </Button>
                </Dialog.Footer>
              </Dialog.Content>
            </Dialog.Root>

            <Flex direction="column" gap={4}>
              {isLoadingHistory && historyItems.length === 0 ? (
                <Flex gap={2} align="center" justify="center" p={4}>
                  <Spinner
                    borderWidth="4px"
                    animationDuration="0.65s"
                    color="#00a1d2"
                    size="lg"
                  />
                  <Text>Loading history...</Text>
                </Flex>
              ) : (
                historyItems.map((e) => (
                  <Box
                    p={3}
                    shadow={e.id === selectedHistoryId ? 'sm' : 'lg'}
                    borderWidth="1px"
                    key={e.id}
                    rounded="md"
                    onClick={() => setSelectedHistoryId(e.id)}
                    bg={e.id === selectedHistoryId ? 'bg.brand' : 'bg.surface'}
                    color={
                      e.id !== selectedHistoryId ? 'brand.600' : 'fg.inverse'
                    }
                    cursor="pointer"
                    border="1px solid"
                    borderColor={
                      e.id === selectedHistoryId
                        ? 'brand.500'
                        : 'border.default'
                    }
                    transition="all 200ms ease"
                    _hover={{
                      bg:
                        e.id === selectedHistoryId
                          ? 'bg.interactive.hover'
                          : 'bg.subtle',
                      borderColor:
                        e.id === selectedHistoryId
                          ? 'brand.600'
                          : 'border.emphasis',
                    }}
                  >
                    <Heading fontSize="lg">{String(e.submitted)}</Heading>
                    <Flex justify="space-between" mt={4}>
                      <Text>{e.source}</Text>
                      <IconButton
                        variant="ghost"
                        aria-label="Open history item"
                        onClick={() => setIsModalOpen(true)}
                        size="sm"
                      >
                        <FiInfo />
                      </IconButton>
                    </Flex>
                  </Box>
                ))
              )}
            </Flex>
          </Drawer.Body>

          <Drawer.Footer>
            <Button
              variant="secondary"
              size="md"
              disabled={!nextToken || isLoadingHistory}
              mr={3}
              onClick={() => getHistory()}
            >
              {isLoadingHistory ? 'Loading...' : 'More items..'}
            </Button>
            <Drawer.ActionTrigger asChild>
              <Button variant="ghost" size="md" mr={3}>
                Cancel
              </Button>
            </Drawer.ActionTrigger>
            <Button
              variant="primary"
              size="md"
              disabled={!selectedQuery}
              onClick={callUpdateFunction}
            >
              Load query
            </Button>
          </Drawer.Footer>
        </Drawer.Content>
      </Drawer.Root>
    </Box>
  );
};

export default HistoryDrawer;
